import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  BabyChangingStation,
  Visibility,
  VisibilityOff,
  AddCircleOutline,
} from "@mui/icons-material";
import FormActions from "components/FormActions";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addNursery as addNurseryApi,
  getClassrooms,
  getRegions,
  getCountries,
} from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/fr";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import staticCountries from "components/data";

type Classroom = { id: number; name: string; nurseryId: number };

const schema = Yup.object().shape({
  firstName: Yup.string().required("This field is required"),
  lastName: Yup.string().required("This field is required"),
  email: Yup.string().email("Invalid email").required("This field is required"),
  mobile: Yup.string().required("This field is required"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Min 8 characters"),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password")], "Passwords do not match"),
  line1: Yup.string().required("This field is required"),
  city: Yup.string().required("This field is required"),
  postcode: Yup.string().required("This field is required"),
});

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const req = (label: string) => (
  <>
    {label} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const AddStaff: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({ resolver: yupResolver(schema) });

  const [submitting, setSubmitting] = useState(false);
  const [dob, setDob] = useState<Dayjs | null>(null);
  const [role, setRole] = useState("");
  const [roleError, setRoleError] = useState(false);
  const [classroomIds, setClassroomIds] = useState<number[]>([]);
  const [classroomError, setClassroomError] = useState(false);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [country, setCountry] = useState("Algeria");
  const [countries, setCountries] = useState<
    { code: string; name: string; displayName: string }[]
  >([]);
  const [regions, setRegions] = useState<
    { code: string; name: string; displayName?: string }[]
  >([]);
  const [regionLabel, setRegionLabel] = useState("City");

  useEffect(() => {
    getClassrooms().then((data) => setClassrooms(data || []));
    getCountries().then((data) =>
      setCountries(
        data.length
          ? data
          : staticCountries.map((c) => ({ ...c, displayName: c.name })),
      ),
    );
  }, [i18n.language]);

  useEffect(() => {
    const countryEntry =
      countries.find((c) => c.name === country) ||
      staticCountries.find((c) => c.name === country);
    if (countryEntry) {
      getRegions(countryEntry.code).then((data) => {
        setRegions(data.regions || []);
        setRegionLabel(data.label || "City");
      });
    } else {
      setRegions([]);
      setRegionLabel("City");
    }
  }, [country, i18n.language]);

  const onSubmit: SubmitHandler<any> = (data) => {
    let hasError = false;
    if (!role) {
      setRoleError(true);
      hasError = true;
    }
    if (role && classrooms.length > 0 && classroomIds.length === 0) {
      setClassroomError(true);
      hasError = true;
    }
    if (hasError) return;

    setSubmitting(true);
    addNurseryApi({
      info: data,
      role,
      dateOfBirth: dob ? dayjs(dob).format("YYYY-MM-DD") : undefined,
      classroomIds,
    })
      .then(() => {
        enqueueSnackbar(t("nursery.added"), { variant: "success" });
        navigate("/nursery");
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.message || "Failed", {
          variant: "error",
        }),
      )
      .finally(() => setSubmitting(false));
  };

  const passwordToggle = (visible: boolean, toggle: () => void) => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={toggle} edge="end" tabIndex={-1}>
        {visible ? (
          <VisibilityOff sx={{ fontSize: 18 }} />
        ) : (
          <Visibility sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/nursery")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <BabyChangingStation sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("nursery.add")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* General Information */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.general_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.first_name"))}
                    fullWidth
                    size="small"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message as string}
                    {...register("firstName")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.last_name"))}
                    fullWidth
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message as string}
                    {...register("lastName")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.email"))}
                    fullWidth
                    size="small"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message as string}
                    {...register("email")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.mobile"))}
                    fullWidth
                    size="small"
                    error={!!errors.mobile}
                    helperText={errors.mobile?.message as string}
                    {...register("mobile")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.language}
                  >
                    <DesktopDatePicker
                      label={t("inputs.birthday")}
                      value={dob}
                      onChange={setDob}
                      inputFormat="DD MMM YYYY"
                      maxDate={dayjs()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="DD MMM YYYY"
                          sx={inputSx}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Password & Role */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("inputs.password")} & {t("inputs.role")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.newPassword"))}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    size="small"
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message as string}
                    {...register("password")}
                    InputProps={{
                      endAdornment: passwordToggle(showPassword, () =>
                        setShowPassword(!showPassword),
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.confirmPassword"))}
                    type={showConfirm ? "text" : "password"}
                    fullWidth
                    size="small"
                    error={Boolean(errors.confirmPassword)}
                    helperText={errors.confirmPassword?.message as string}
                    {...register("confirmPassword")}
                    InputProps={{
                      endAdornment: passwordToggle(showConfirm, () =>
                        setShowConfirm(!showConfirm),
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={roleError && !role}
                  >
                    <InputLabel>{req(t("inputs.role"))}</InputLabel>
                    <Select
                      label={req(t("inputs.role"))}
                      value={role}
                      onChange={(e) => {
                        setRole(e.target.value);
                        setRoleError(false);
                        setClassroomIds([]);
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="ROOM_LEADER">
                        {t("labels.room_leader")}
                      </MenuItem>
                      <MenuItem value="NURSERY_PRACTITIONER">
                        {t("labels.nursery_practitioner")}
                      </MenuItem>
                    </Select>
                    {roleError && !role && (
                      <FormHelperText>This field is required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  {classrooms.length === 0 && role ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Chip
                        icon={<AddCircleOutline sx={{ fontSize: 18 }} />}
                        label={t("classroom.create_first")}
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={() => navigate("/class")}
                        sx={{
                          borderRadius: 2,
                          fontWeight: 500,
                          cursor: "pointer",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>
                  ) : (
                    <FormControl
                      fullWidth
                      size="small"
                      error={classroomError && classroomIds.length === 0}
                      disabled={!role}
                    >
                      <InputLabel>{t("inputs.classroom")}</InputLabel>
                      <Select
                        multiple
                        label={t("inputs.classroom")}
                        value={classroomIds}
                        onChange={(e) => {
                          const v = e.target.value;
                          setClassroomIds(
                            typeof v === "string"
                              ? v.split(",").map(Number)
                              : (v as number[]),
                          );
                          setClassroomError(false);
                        }}
                        renderValue={(selected) =>
                          (selected as number[])
                            .map(
                              (id) =>
                                classrooms.find((c) => c.id === id)?.name ?? id,
                            )
                            .join(", ")
                        }
                        sx={{ borderRadius: 2 }}
                      >
                        {classrooms.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {classroomError && classroomIds.length === 0 && (
                        <FormHelperText>This field is required</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Address */}
          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("address")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.address_line1"))}
                    fullWidth
                    size="small"
                    error={!!errors.line1}
                    helperText={errors.line1?.message as string}
                    {...register("line1")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={t("inputs.address_line2")}
                    fullWidth
                    size="small"
                    {...register("line2")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  {regions.length > 0 ? (
                    <FormControl fullWidth size="small" error={!!errors.city}>
                      <InputLabel>{req(regionLabel)}</InputLabel>
                      <Select
                        label={req(regionLabel)}
                        defaultValue=""
                        {...register("city")}
                        sx={{ borderRadius: 2 }}
                      >
                        {regions.map((r) => (
                          <MenuItem key={r.code} value={r.name}>
                            {r.code} - {r.displayName || r.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.city && (
                        <FormHelperText>
                          {errors.city?.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      label={req(t("inputs.city"))}
                      fullWidth
                      size="small"
                      error={!!errors.city}
                      helperText={errors.city?.message as string}
                      {...register("city")}
                      sx={inputSx}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={req(t("inputs.post_code"))}
                    fullWidth
                    size="small"
                    error={!!errors.postcode}
                    helperText={errors.postcode?.message as string}
                    {...register("postcode")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.country"))}</InputLabel>
                    <Select
                      label={req(t("inputs.country"))}
                      value={country}
                      {...register("country", {
                        onChange: (e) => setCountry(e.target.value),
                      })}
                      sx={{ borderRadius: 2 }}
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.code} value={c.name}>
                          {c.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <FormActions
            onCancel={() => navigate("/nursery")}
            submitLabel={t("buttons.add")}
            submitting={submitting}
          />
        </form>
      </Container>
    </>
  );
};

export default AddStaff;
